import { useAtomValue } from 'jotai';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import { endpointPortfolioForwardProjectionGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import {
  ForwardReturnsWidget,
  type ForwardReturnsData,
} from './ForwardReturnsWidget';
import { GET_PORTFOLIO_NAME } from '~/graphql/queries/portfolio';
import { GET_INSTRUMENT_BY_ISIN } from '~/graphql/queries/instruments';
import { useHasBenchmark } from '~/hooks/useHasBenchmark';

export const ForwardReturnsWidgetWrapper: React.FC<{
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
}> = ({ handleRemoveWidget, widgetId, title }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const [projectionData, setProjectionData] =
    useState<ForwardReturnsData | null>(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [projectionError, setProjectionError] = useState<Error | null>(null);
  const [benchmarkName, setBenchmarkName] = useState<string | null>(null);

  // Check if portfolio has a benchmark using the dedicated endpoint
  const { hasBenchmark, isLoading: hasBenchmarkLoading } =
    useHasBenchmark(portfolioId);

  // Fetch portfolio data to get benchmark_index for instrument name lookup
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Get benchmark_index and portfolio name from portfolio
  const benchmarkIndex = useMemo(() => {
    return portfolioData?.EQPortfolio?.[0]?.benchmark_index;
  }, [portfolioData]);

  const portfolioName = useMemo(() => {
    return portfolioData?.EQPortfolio?.[0]?.name;
  }, [portfolioData]);

  // Fetch benchmark instrument by ISIN (only if portfolio has a benchmark)
  const { data: benchmarkInstrument, loading: benchmarkLoading } = useQuery(
    GET_INSTRUMENT_BY_ISIN,
    {
      variables: { isin: benchmarkIndex },
      skip: !hasBenchmark || !benchmarkIndex,
    },
  );

  // Update benchmark name when instrument data changes
  useEffect(() => {
    if (benchmarkInstrument?.EQInstrument?.[0]?.name) {
      setBenchmarkName(benchmarkInstrument.EQInstrument[0].name);
    } else {
      setBenchmarkName(null);
    }
  }, [benchmarkInstrument]);

  // Fetch forward projection data using REST API
  const fetchProjectionData = useCallback(async () => {
    if (!latestSnapshotId) return null;

    try {
      setProjectionLoading(true);
      setProjectionError(null);

      const response = await endpointPortfolioForwardProjectionGET({
        snapshot_id: latestSnapshotId,
        horizon_days: 63,
        include_current: true,
        include_benchmark: hasBenchmark,
      });
      setProjectionData(response as ForwardReturnsData);
      return response;
    } catch (error) {
      console.error('Failed to fetch forward projection data:', error);

      // Check if it's a 400 error (no data found) - set data to empty instead of error
      if (
        error &&
        typeof error === 'object' &&
        'statusCode' in error &&
        error.statusCode === 400
      ) {
        setProjectionData(null);
        setProjectionError(null);
      } else {
        setProjectionError(error as Error);
      }
      return null;
    } finally {
      setProjectionLoading(false);
    }
  }, [latestSnapshotId, hasBenchmark]);

  // Fetch projection data only when all prerequisites are ready
  useEffect(() => {
    // Don't fetch if no snapshot
    if (!latestSnapshotId) return;

    // Wait for benchmark check to complete
    if (hasBenchmarkLoading) {
      return;
    }

    // If we have a benchmark, wait for the instrument name query to complete
    if (hasBenchmark && benchmarkLoading) {
      return;
    }

    // All prerequisites ready, fetch the data
    fetchProjectionData();
  }, [
    latestSnapshotId,
    hasBenchmark,
    hasBenchmarkLoading,
    benchmarkLoading,
    fetchProjectionData,
  ]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      fetchProjectionData();
    }
  }, [triggerHardRefetch, fetchProjectionData]);

  const refetch = useMemo(() => {
    return () => {
      fetchProjectionData();
    };
  }, [fetchProjectionData]);

  return (
    <ForwardReturnsWidget
      data={projectionData}
      isLoading={
        projectionLoading ||
        hasBenchmarkLoading ||
        (hasBenchmark && benchmarkLoading)
      }
      error={projectionError}
      refetch={refetch}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={title}
      snapshotId={latestSnapshotId}
      benchmarkName={benchmarkName}
      portfolioId={portfolioId}
      portfolioName={portfolioName}
    />
  );
};
